/* eslint-disable no-inline-comments */
/* eslint-disable line-comment-position */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { getPanelCenter } from './get-panel-center';
import { Panel } from '../panel.class';
import { PanelCorners } from '../types';
import { Axis, Vertex3 } from '../../../spatial-classes';

/**
 * Options for calculating the corner points of a panel.
 */
export type GetPanelCornerPointsOptions = {
	/**
	 * If true, the panel's corner points will be rotated based on the panel's origin angle.
	 */
	rotate?: boolean;

	/**
	 * If true, the calculation will translate the panel's points relative to the island's center.
	 * If false or omitted, the points are calculated in the panel's local coordinate system.
	 * This is useful for toggling between absolute and relative positioning.
	 */
	translate?: boolean;

	/**
	 * If true, the resulting corner points will be rounded to the nearest sixteenth of a unit.
	 * This is typically used to ensure measurements conform to standard increments.
	 * If false, the points are left as calculated.
	 */
	nearestSixteenth?: boolean;

	/**
	 * If true, the resulting corner points will be read only.
	 * This is useful for preventing accidental modification of the points.
	 */
	lock?: boolean;

	/**
	 * If true, the calculation will use the inside face of the panel.
	 * If false, the calculation will use the outside face of the panel.
	 * If null, the calculation will use the center of the panel.
	 */
	inside?: boolean | null;

	/**
	 * If true, the calculation will use the padding to offset the corner points.
	 * This is useful for calculating the corner points of a panel with a padding.
	 */
	padding?: {
		left?: number;
		right?: number;
		top?: number;
		bottom?: number;
		front?: number;
		back?: number;
	};
}

/**
 * Calculates the corner points and center point of a panel.
 *
 * This CFN (class function) determines the four corner points of a panel in its local coordinate system,
 * with options for translation to island coordinates, rotation, and thickness adjustments for inside/outside faces.
 * The function supports various coordinate transformations and precision rounding.
 *
 * @param panel - The Panel instance for which to calculate corner points
 * @param options - Calculation parameters including inside face, rotation, translation, and rounding options
 * @param visitedPanels - Set of panel IDs to prevent infinite recursion (currently unused but kept for compatibility)
 * @returns PanelCorners object with corner points, axis, center, and origin, or null if circular reference detected
 */
export const getPanelCornerPoints = (
	panel: Panel,
	options: GetPanelCornerPointsOptions,
	visitedPanels: Set<string> = new Set()
): PanelCorners => {
	// Extract and set default values for calculation options
	const { inside = null, translate = false, rotate = false, nearestSixteenth = true, lock = true } = options;
	const { left = 0, right = 0, top = 0, bottom = 0, front = 0, back = 0 } = options.padding ?? {};
	const zero = new Vertex3();

	// Prevent infinite recursion if this panel was already processed
	if (visitedPanels.has(panel.id)) {
		console.warn(`Circular connection detected in panel ${panel.id}, returning null for getCornerPoints`);
		return null;
	}
	visitedPanels.add(panel.id);

	// Calculate panel dimensions for corner positioning
	const halfPanelWidth = panel.size.width / 2;
	const halfPanelHeight = panel.size.height / 2;
	const halfPanelDepth = panel.size.depth / 2;

	// Calculate Z-offset for inside/outside face positioning
	// null = centered (0), true = inside face (-halfDepth), false = outside face (+halfDepth)
	const insideOffset = inside === null ? 0 : (inside ? -halfPanelDepth : halfPanelDepth);
	// const offset = new Vertex3({ dataIn: { z: insideOffset }});

	// Initialize panel corners in local coordinate system
	// Corner order: [top-left, top-right, bottom-right, bottom-left]
	const panelCorners = {
		corners: [
			new Vertex3({ dataIn: { x: -halfPanelWidth - left, y: -panel.size.height - top, z: insideOffset + front }}),
			new Vertex3({ dataIn: { x: halfPanelWidth + right, y: -panel.size.height - top, z: insideOffset + front }}),
			new Vertex3({ dataIn: { x: halfPanelWidth + right, y: top, z: insideOffset + front }}),
			new Vertex3({ dataIn: { x: -halfPanelWidth - left, y: top, z: insideOffset + front }})
		],
		axis: new Axis({ dataIn: { y: rotate ? panel.getOriginAngle() : 0 }}),
		center: new Vertex3({ dataIn: { x: (right - left) / 2, y: -halfPanelHeight - ((top - bottom) / 2), z: insideOffset + ((front - back) / 2) }})
	};

	// Rotate corners around center if rotation is enabled and angle is not zero
	if (rotate && panelCorners.axis.y !== 0) {
		// const rotationAngle = panel.axis.y === 180 ? 180 : (panelCorners.axis.y - 180) % 360;
		// panelCorners.corners.forEach((corner) => { corner.rotateAxis(panelCorners.center, rotationAngle, 'y'); });
		panelCorners.corners.forEach((corner) => { corner.rotateAxis(zero, -panelCorners.axis.y, 'y'); });
		panelCorners.center.rotateAxis(zero, -panelCorners.axis.y, 'y');
	}

	// Translate corners to island coordinate system if requested
	if (translate) {
		const panelCenter = getPanelCenter(panel, { nearestSixteenth: false }, new Set());
		// panelCorners.center.add(new Vertex3({ dataIn: { x: panelCenter.x, z: panelCenter.z }}));
		// panelCorners.corners.forEach((corner) => { corner.add(new Vertex3({ dataIn: { x: panelCenter.x, z: panelCenter.z }})); });
		panelCorners.corners.forEach((corner) => {
			corner.x += panelCenter.x;
			corner.z += panelCenter.z;
		});
		panelCorners.center.x += panelCenter.x;
		panelCorners.center.z += panelCenter.z;
	}

	// Round coordinates to nearest sixteenth if precision rounding is enabled
	if (nearestSixteenth) {
		panelCorners.corners.forEach((corner) => { corner.roundNearestSixteenth(); });
		panelCorners.center.roundNearestSixteenth();
	}

	// Make points read-only if lock is enabled to prevent accidental modification
	if (lock) {
		panelCorners.corners.forEach((corner) => { corner.readOnly = true; });
		panelCorners.center.readOnly = true;
	}

	// Debug logging (commented out for production)
	// if (translate && rotate) {
	// console.log('getPanelCornerPoints', visitedPanels.size, panel.label, inside, {
	// 	center: panelCorners.center.clone().roundNearestSixteenth(),
	// 	corners: panelCorners.corners.map((corner) => corner.clone().roundNearestSixteenth()),
	// 	padding: options.padding
	// }, insideOffset);
	// }

	return panelCorners;
}
